/**
 * Pure builder for the printable ORDER LIST as a pdfmake document definition —
 * **A4 LANDSCAPE, one row per ORDER**.
 *
 * Product owner: *"print all is a printable list, likey landscape to fit all
 * columns on."* This is the "Print all" / "Export" companion to the order-list
 * pages, not a stack of order documents: nine columns lifted from the table
 * layout's own vocabulary, one line per order, sized to the page rather than to
 * a screen.
 *
 * ── Why this is a SECOND file and not a landscape mode on `orderPdf.js` ──────
 * 🚨 **Do not merge the two, and do not add an orientation flag to `orderPdf.js`
 * to "share" this.** That file builds a SINGLE order for a SUPPLIER, and it is
 * portrait by construction, not by preference:
 *   - its `HEADER_LINE_WIDTH = 523` is hard-coded A4 **portrait** arithmetic
 *     (595 − 36 − 36), so a landscape page would draw its rule 247pt short;
 *   - `orderPdf.test.js:249` pins `pageOrientation === 'portrait'`, i.e. the
 *     orientation is a contract of that document, not an incidental value.
 * Two documents, two files. They share the house STYLE (colour constants, the
 * footer shape, `defaultStyle`, `info`) and nothing else, exactly as
 * `budgetManagePdf.js` shares it without importing from `budgetVariancePdf.js`.
 *
 * No dependency on pdfmake itself — only declarative doc-def data is returned,
 * so the layout stays pure and unit-testable (see `orderListPdf.test.js`).
 * Mirrors the conventions in `orderPdf.js` / `budgetManagePdf.js`.
 *
 * ── The shape closest to this one ────────────────────────────────────────────
 * `budgetManagePdf.js` (A4 landscape tabular list, `pageOrientation: 'landscape'`
 * at :261) is the model for the page wrapper; `orderPdf.js`'s `lineItemsTable`
 * is the model for the table itself (navy header row, zebra body, thin grey
 * rules, the single spanning "nothing here" row).
 */

import { formatCurrency } from './formatCurrency';
import { EM_DASH } from './moneyOrAbsent';
import { getOrderNumber } from './orderTitle';
import { poIsPrefixed } from './orderPdfMeta';
import { STATUS_LABELS } from '../constants/colors';
import {
  buildListContent, buildListDoc, CONTENT_WIDTH, SECONDARY_FG, numeric, text, dateText, td, cap,
} from './listPdf';

// 🔀 2026-09-27: the geometry, the house style, the generic cell helpers
// (`numeric`, `text`, `dateText`, `td`), the table and the page wrapper MOVED
// verbatim to `./listPdf.js` when the hires pages needed the same list document.
// What stays here is what is ORDER-shaped: the helpers below and the columns.
// 🔀 2026-09-28: `cap` joined them — see `listPdf.js`'s own docblock.
export { CONTENT_WIDTH };

// ── Small pure helpers ──────────────────────────────────────────────────────

/**
 * The stored PO number, rendered.
 *
 * 🔑 `po_number` carries a `PO-` prefix **in the database** since 2026-08-08
 * (`server/helpers/poNumber.js`; product owner: *"Make all Po numbers start with
 * PO-"*), so the common case is printed verbatim. A legacy row the backfill has
 * not reached still holds a bare `147` and gets the prefix applied for display.
 *
 * 🪤 The already-prefixed test is `poIsPrefixed` from `utils/orderPdfMeta.js` —
 * **imported, never re-implemented**. It REQUIRES a separator, so `POWER-1` is
 * not mistaken for a prefixed value and mangled into `PO-WER-1`. Re-typing the
 * regex here is exactly how `PO-PO-147` shipped once already.
 */
function poText(order) {
  const raw = order && order.po_number;
  if (raw == null || String(raw).trim() === '') return '';
  const value = String(raw).trim();
  return poIsPrefixed({ po_number: value }) ? value : `PO-${value}`;
}

/**
 * How many line items this order has.
 *
 * Mirrors the precedence of `orderItemCount` in `components/OrderTableView.js`
 * (`item_count` → the item array → the degraded single-row source) rather than
 * importing it: a pure `utils/` builder must not depend on a React component,
 * and that component is a page-layout module holding JSX. The PRECEDENCE is the
 * shared thing, and it is short enough to state twice with the reason written
 * down; if a third caller appears, extract it.
 *
 * `item_count` is a `COUNT(*)` and therefore a STRING on PostgreSQL — see
 * `numeric` above.
 */
function itemCount(order) {
  const declared = numeric(order && order.item_count);
  if (declared != null) return declared;
  if (Array.isArray(order && order.items)) return order.items.length;
  if (Array.isArray(order && order.preview_items)) return order.preview_items.length;
  return order && order.first_item_description ? 1 : 0;
}

/**
 * The status, as TEXT.
 *
 * 🖨️ **Never a coloured badge.** This document is printed, routinely in
 * greyscale, so a colour-only status is a status nobody can read — the
 * sole-carrier problem CLAUDE.md records against `DueIndicator`, arriving on
 * paper where there is no hover, no `title` and no `aria-label` to fall back on.
 * The word IS the signal here.
 *
 * Falls back the way `components/StatusBadge.js` does, so an unrecognised status
 * still renders as words rather than as a raw enum (`partially_delivered`).
 */
function statusText(order) {
  const raw = order && order.status;
  if (raw == null || String(raw).trim() === '') return EM_DASH;
  const key = String(raw).trim();
  return STATUS_LABELS[key] || cap(key.replace(/_/g, ' '));
}

/**
 * The order total.
 *
 * 🚨 **A missing total prints a DASH, not `£0.00`, and that is a deliberate
 * departure from the retired card row's `order.order_total ??
 * order.total_amount ?? 0`.** `order_total` is a DERIVED field — there is no
 * such column on `orders`, on either engine — and every list endpoint has to
 * compute it. One of the three did not, and the `?? 0` fallback is precisely
 * what made that silent: **the To Approve page displayed £0.00 for every order
 * in production**, because a missing derived field rendered as a *plausible*
 * number instead of an obvious gap. On a page that is printed and handed around,
 * a plausible wrong number is the worst possible failure mode.
 */
function totalText(order) {
  const value = numeric(order && order.order_total);
  const legacy = value == null ? numeric(order && order.total_amount) : null;
  const amount = value != null ? value : legacy;
  return amount == null ? EM_DASH : formatCurrency(amount);
}

// ── Columns ─────────────────────────────────────────────────────────────────

/**
 * THE nine columns, and the ONLY place any of them is described.
 *
 * 🔑 **Header cells AND body cells are both generated from this array**, so the
 * two cannot desync. That is not tidiness: `components/OrderTableView.js` builds
 * its header from an array while its body cells are hand-ordered JSX, and
 * CLAUDE.md records the consequence — *"Move one without the other and every
 * column right of the mistake renders under the wrong heading — silently,
 * because a `<td>` is a `<td>`"*. A pdfmake cell is likewise just an object, so
 * the same defect is available here for free. Generating both removes it
 * structurally rather than pinning it with a test.
 *
 * ── The widths, and the arithmetic behind them ───────────────────────────────
 * Printable width is **770pt** (`CONTENT_WIDTH` = 842 − 36 − 36). The table's
 * layout adds `paddingLeft` 4 + `paddingRight` 4 to every column, so nine
 * columns spend **72pt** on padding before any content:
 *
 *     fixed columns   78 + 52 + 52 + 90 + 30 + 66 + 74            = 442pt
 *     padding         9 columns × 8                               =  72pt
 *     remaining       770 − 442 − 72                              = 256pt
 *     the two `*`     256 ÷ 2                                     = 128pt each
 *
 * **Job Name and Supplier are the flexible (`'*'`) pair** because they are the
 * only free-text columns: everything else is a date, a count, a currency figure
 * or a value from a fixed vocabulary, all of which have a knowable worst case,
 * while a job name or a supplier name has none. Giving the slack to the two
 * columns that can actually use it is what keeps the fixed ones from being sized
 * for a case that never occurs.
 */
export const ORDER_LIST_COLUMNS = [
  {
    key: 'po',
    label: 'PO / Order No.',
    // Two stacked lines — `PO-0012` over `ORD-042` — the same one-column
    // treatment the table layout uses, for the same measured reason: 1,728 of
    // the 1,736 orders in the dev database have no PO at all, so a dedicated PO
    // column would be blank on 99.5% of rows.
    width: 78,
    align: 'left',
    cell: (order) => {
      const po = poText(order);
      // 🔀 `getOrderNumber` (per-department `ORD-004`), not `getOrderRef`
      // (`AURORA-ORD-042`), 2026-08-12. Label `PO / Order No.` unchanged.
      const ref = text(getOrderNumber(order));
      // 🔴 No PO → the order NUMBER alone, with no blank line above it and no
      // muted dash. A dash on every one of those rows is noise, not information;
      // the cell is never empty, which is the property that made the table
      // layout's version work.
      if (!po) return { text: ref, fontSize: 8 };
      return {
        stack: [
          { text: po, fontSize: 8, bold: true },
          { text: ref, fontSize: 7, color: SECONDARY_FG },
        ],
      };
    },
  },
  {
    key: 'ordered',
    label: 'Ordered',
    width: 52,
    align: 'left',
    // `order_date`, NEVER `created_at`. They differ on 120 of the 1,736 orders
    // in the dev database, because `order_date` is settable on the form and
    // back-dating a paper order is ordinary practice.
    cell: (order) => td(dateText(order && order.order_date)),
  },
  {
    key: 'needed_by',
    label: 'Needed By',
    width: 52,
    align: 'left',
    /**
     * 🚨 `required_by_date` is a SERVER field and must never be re-derived here.
     * It comes from `computeSlackDetail`, which captures the deadline in the
     * SAME branch that lowers the minimum slack, so it is *by construction* the
     * binding deadline. Both obvious client shortcuts are wrong:
     * `custom_needed_by_date` is only ONE branch of the cascade (a legacy item's
     * own `needed_by_date` still outranks it), and "the earliest item date" is
     * wrong because a long lead time can make a LATER-dated item the binding
     * constraint. An order with no deadline prints a dash — not a guess.
     */
    cell: (order) => td(dateText(order && order.required_by_date)),
  },
  {
    key: 'ordered_by',
    label: 'Ordered By',
    width: 90,
    align: 'left',
    // 🪤 The COLUMN is "Ordered By"; the FIELD is `requested_by`. The two differ
    // and always have — the same mismatch that makes a wrong `sortField` sort
    // nothing on the table pages.
    cell: (order) => td(text(order && order.requested_by)),
  },
  {
    key: 'job_name',
    label: 'Job Name',
    width: '*',
    align: 'left',
    // 🪤 The COLUMN is "Job Name"; the FIELD is `job_rig`.
    cell: (order) => td(text(order && order.job_rig)),
  },
  {
    key: 'supplier',
    label: 'Supplier',
    width: '*',
    align: 'left',
    cell: (order) => td(text(order && order.supplier_name)),
  },
  {
    key: 'items',
    label: 'Items',
    width: 30,
    align: 'center',
    // A COUNT, not a description. One row is one ORDER, so the line items are
    // summarised by their number and nothing else.
    cell: (order) => td(String(itemCount(order)), 'center'),
  },
  {
    key: 'order_total',
    label: 'Order Total',
    width: 66,
    align: 'right',
    cell: (order) => td(totalText(order), 'right', { bold: true }),
  },
  {
    key: 'status',
    label: 'Status',
    width: 74,
    align: 'left',
    // Plain text — no `color`, no `fillColor`. See `statusText` above.
    cell: (order) => td(statusText(order)),
  },
];

/** The declared pixel widths only — the `'*'` columns are excluded. */
export const ORDER_LIST_FIXED_WIDTH = ORDER_LIST_COLUMNS
  .filter(c => typeof c.width === 'number')
  .reduce((sum, c) => sum + c.width, 0);

// ── The document ────────────────────────────────────────────────────────────

const ORDER_NOUN = Object.freeze({ one: 'order', many: 'orders' });

/**
 * Just the pdfmake `content[]` slice for the ORDER list — `./listPdf.js`'s
 * `buildListContent` over `ORDER_LIST_COLUMNS`. Exported so the wrapper and the
 * content are separately assertable (`orderListPdf.test.js`).
 */
export function buildOrderListContent({
  orders = [],
  headerTitle = 'Orders',
  departmentName = '',
  filterSummary = '',
  totalCount = null,
} = {}) {
  return buildListContent({
    rows: orders, columns: ORDER_LIST_COLUMNS, noun: ORDER_NOUN,
    headerTitle, departmentName, filterSummary, totalCount,
  });
}

/** The whole A4-landscape ORDER list document. */
export function buildOrderListDoc(args = {}) {
  const { orders = [], headerTitle = 'Orders', ...rest } = args;
  return buildListDoc({
    ...rest, rows: orders, columns: ORDER_LIST_COLUMNS, noun: ORDER_NOUN, headerTitle, subject: 'Order List',
  });
}
